import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CheckCircle2, Eraser, PenLine, Loader2, Shield, AlertTriangle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Public signing page — reached via /sign/:token. No authentication required;
 * the token itself is the credential (burned once signed).
 */
const PublicSign = () => {
  const { token } = useParams();
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [contract, setContract] = useState(null);
  const [error, setError] = useState(null);
  const [alreadySigned, setAlreadySigned] = useState(false);

  const [typedName, setTypedName] = useState("");
  const [intent, setIntent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [signed, setSigned] = useState(null);

  const canvasRef = useRef(null);
  const drawing = useRef(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/contracts/sign/${token}`);
        if (data.already_signed) {
          setAlreadySigned(true);
          setContract(data);
        } else {
          setContract(data);
        }
      } catch (e) {
        setError(e?.response?.data?.detail || "Link invalid or expired");
      }
      setLoading(false);
    })();
  }, [token]);

  // Canvas drawing
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#0F172A";
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, c.width, c.height);

    const pos = (e) => {
      const r = c.getBoundingClientRect();
      const t = e.touches ? e.touches[0] : e;
      return { x: (t.clientX - r.left) * (c.width / r.width),
               y: (t.clientY - r.top) * (c.height / r.height) };
    };
    const down = (e) => { e.preventDefault(); drawing.current = true; const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
    const move = (e) => { if (!drawing.current) return; e.preventDefault(); const p = pos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); };
    const up = () => { drawing.current = false; };
    c.addEventListener("mousedown", down); c.addEventListener("mousemove", move); c.addEventListener("mouseup", up); c.addEventListener("mouseleave", up);
    c.addEventListener("touchstart", down, { passive: false }); c.addEventListener("touchmove", move, { passive: false }); c.addEventListener("touchend", up);
    return () => {
      c.removeEventListener("mousedown", down); c.removeEventListener("mousemove", move); c.removeEventListener("mouseup", up); c.removeEventListener("mouseleave", up);
      c.removeEventListener("touchstart", down); c.removeEventListener("touchmove", move); c.removeEventListener("touchend", up);
    };
  }, [contract]);

  const clearCanvas = () => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, c.width, c.height);
  };

  const isCanvasBlank = () => {
    const c = canvasRef.current;
    if (!c) return true;
    const ctx = c.getContext("2d");
    const d = ctx.getImageData(0, 0, c.width, c.height).data;
    let coloredPixels = 0;
    for (let i = 0; i < d.length; i += 40) {
      if (d[i] < 240 || d[i + 1] < 240 || d[i + 2] < 240) coloredPixels++;
      if (coloredPixels > 20) return false;
    }
    return true;
  };

  const submit = async () => {
    if (!typedName.trim()) return toast.error("Please type your full name");
    if (!intent) return toast.error("Please confirm your intent to sign");
    if (isCanvasBlank()) return toast.error("Please draw your signature in the box");

    setBusy(true);
    try {
      const png = canvasRef.current.toDataURL("image/png");
      const { data } = await axios.post(`${API}/api/contracts/sign/${token}`, {
        typed_name: typedName.trim(),
        intent_to_sign: intent,
        signature_png: png,
      });
      setSigned(data);
      toast.success("Contract signed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Signing failed");
    }
    setBusy(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50">
        <Loader2 className="w-6 h-6 animate-spin text-brand-red" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50 p-6">
        <Card className="p-8 max-w-md text-center rounded-2xl">
          <AlertTriangle className="w-10 h-10 text-red-500 mx-auto mb-3" />
          <div className="font-heading text-lg font-bold">Link not valid</div>
          <p className="text-sm text-slate-500 mt-2">{error}</p>
          <p className="text-xs text-slate-400 mt-4">Please contact your manager for a fresh signing link.</p>
        </Card>
      </div>
    );
  }
  if (alreadySigned) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50 p-6">
        <Card className="p-8 max-w-md text-center rounded-2xl">
          <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-3" />
          <div className="font-heading text-lg font-bold">Already signed</div>
          <p className="text-sm text-slate-500 mt-2">
            This contract was signed on {contract?.signed_at?.slice(0, 10) || "record"}.
          </p>
        </Card>
      </div>
    );
  }
  if (signed) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50 p-6">
        <Card className="p-8 max-w-md text-center rounded-2xl">
          <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
          <div className="font-heading text-xl font-bold">Contract signed</div>
          <p className="text-sm text-slate-500 mt-2">
            Thank you. Your signature has been recorded and the PDF is now locked.
          </p>
          <div className="mt-4 p-3 bg-slate-50 rounded text-[10px] text-slate-500 font-mono break-all">
            SHA-256: {signed.pdf_hash}
          </div>
          <p className="text-xs text-slate-400 mt-3">You may close this page.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-16">
      {/* Header */}
      <div className="bg-brand-navy text-white px-4 py-4">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <button onClick={() => nav(-1)} data-testid="sign-back"
                   className="p-2 -ml-2 rounded-lg hover:bg-white/10 text-white/80 hover:text-white">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
          </button>
          <Shield className="w-5 h-5 text-emerald-400" />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/60 font-mono">Secure signing session</div>
            <div className="font-heading font-bold text-base truncate">{contract?.title}</div>
            <div className="text-xs text-white/60">Ref: {contract?.contract_ref}</div>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto p-4 space-y-4">
        {!contract?.approved && (
          <Card className="p-3 rounded-2xl bg-amber-50 border-amber-200 text-xs text-amber-800">
            ⚠️ This template is still marked <strong>DRAFT</strong>. Please only sign after your manager has confirmed
            the wording has been reviewed by a solicitor.
          </Card>
        )}

        {/* Contract preview */}
        <Card className="p-6 rounded-2xl border-brand-border" data-testid="sign-preview">
          {(contract?.blocks || []).map((b, i) => {
            switch (b.type) {
              case "h1": return <h1 key={i} className="font-heading text-2xl font-bold text-brand-navy mt-2">{b.text}</h1>;
              case "h2": return <h2 key={i} className="font-heading text-lg font-bold text-brand-navy mt-4">{b.text}</h2>;
              case "h3": return <h3 key={i} className="font-heading text-base font-semibold text-brand-navy mt-3">{b.text}</h3>;
              case "p":  return <p key={i} className="text-sm text-slate-700 mt-2 leading-relaxed">{b.text}</p>;
              case "ul": return <ul key={i} className="list-disc pl-5 text-sm text-slate-700 mt-2 space-y-0.5">{b.items.map((it, j) => <li key={j}>{it}</li>)}</ul>;
              case "ol": return <ol key={i} className="list-decimal pl-5 text-sm text-slate-700 mt-2 space-y-0.5">{b.items.map((it, j) => <li key={j}>{it}</li>)}</ol>;
              case "kv": return (
                <div key={i} className="grid grid-cols-2 gap-1 mt-3 text-xs">
                  {b.rows.map(([k, v], j) => (
                    <React.Fragment key={j}>
                      <div className="bg-slate-50 px-2 py-1 border border-slate-200 font-medium">{k}</div>
                      <div className="px-2 py-1 border border-slate-200">{v}</div>
                    </React.Fragment>
                  ))}
                </div>
              );
              case "clause": return (
                <div key={i} className="mt-4">
                  <div className="font-semibold text-sm text-brand-navy">{b.n}. {b.title}</div>
                  {b.paras.map((p, j) => <p key={j} className="text-sm text-slate-700 mt-1.5 leading-relaxed">{p}</p>)}
                </div>
              );
              case "signature": return (
                <div key={i} className="mt-6 text-xs text-slate-500 italic border-t border-slate-200 pt-3">
                  Signature block will be embedded in the final PDF.
                </div>
              );
              default: return null;
            }
          })}
        </Card>

        {/* Signature capture */}
        <Card className="p-4 rounded-2xl border-brand-border space-y-3">
          <div className="flex items-center gap-2">
            <PenLine className="w-4 h-4 text-brand-red" />
            <div className="font-semibold text-sm">Your signature</div>
          </div>
          <div>
            <Label className="text-xs">Type your full legal name</Label>
            <Input value={typedName} onChange={(e) => setTypedName(e.target.value)}
                    placeholder="First and last name" data-testid="sign-typed" />
          </div>
          <div>
            <Label className="text-xs mb-2 block">Draw your signature below</Label>
            <div className="relative border border-slate-300 rounded-lg overflow-hidden bg-white">
              <canvas ref={canvasRef} width={800} height={220}
                       className="w-full h-40 touch-none cursor-crosshair" data-testid="sign-canvas" />
              <button type="button" onClick={clearCanvas}
                       className="absolute top-2 right-2 text-xs bg-white/90 border border-slate-200 rounded px-2 py-1 hover:bg-slate-50 flex items-center gap-1"
                       data-testid="sign-clear">
                <Eraser className="w-3 h-3" /> Clear
              </button>
            </div>
          </div>
          <label className="flex items-start gap-2 text-xs text-slate-700 p-2 bg-slate-50 rounded-lg cursor-pointer">
            <input type="checkbox" checked={intent} onChange={(e) => setIntent(e.target.checked)}
                    className="mt-0.5" data-testid="sign-intent" />
            <span>
              I confirm that I have read the contract above and I intend to sign it electronically.
              I consent to my IP address, browser and device details being recorded as part of the audit trail
              under the Electronic Communications Act 2000.
            </span>
          </label>
          <Button onClick={submit} disabled={busy}
                   className="w-full h-11 bg-brand-red hover:bg-brand-red-hover"
                   data-testid="sign-submit">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
            Sign contract
          </Button>
        </Card>
      </div>
    </div>
  );
};

export default PublicSign;
